import { useState } from 'react'
import { DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import { useTranslate, addDays, getStartOfLastMonth, getEndOfLastMonth, getStartOfLastYear, getEndOfLastYear, useWindowDimensions } from 'utils'
import { DatePickerWrapper, MainWrapper, MenuWrapper, TextWrapper } from './analytics-chart-navigation.style'

type AnalyticsChartNavigationProps = {
    startDate: Date
    endDate: Date
    onStartDateChange: (date: Date) => void
    onEndDateChange: (date: Date) => void
}

type Period = '' | 'today' | 'yesterday' | 'lastSevenDays' | 'lastThirtyDays' | 'lastMonth' | 'lastYear'

export const AnalyticsChartNavigation = ({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
}: AnalyticsChartNavigationProps) => {
    const { __ } = useTranslate()
    const { SM, MD } = useWindowDimensions()

    const [selectedPeriod, setSelectedPeriod] = useState<Period>('lastThirtyDays')

    const handlePeriodChange = (value: Period) => {
        const today = new Date()
        let start: Date = new Date()
        let end: Date = new Date()

        // NOTE: last day in end dates is exclusive
        switch (value) {
            case 'today':
                start = addDays(today, 0)
                end = addDays(today, 1)
                break
            case 'yesterday':
                start = addDays(today, -1)
                end = addDays(today, 0)
                break
            case 'lastSevenDays':
                start = addDays(today, -7)
                end = addDays(today, 0)
                break
            case 'lastThirtyDays':
                start = addDays(today, -30)
                end = addDays(today, 0)
                break
            case 'lastMonth':
                start = getStartOfLastMonth()
                end = addDays(getEndOfLastMonth(), 1)
                break
            case 'lastYear':
                start = getStartOfLastYear()
                end = addDays(getEndOfLastYear(), 1)
                break
            default:
                break
        }

        setSelectedPeriod(value)
        onStartDateChange(start)
        onEndDateChange(end)
    }
    
    return (
        <MainWrapper>
            <TextWrapper></TextWrapper>
            <MenuWrapper>
                <Select
                    value={selectedPeriod}
                    options={[
                        { value: 'today', label: `${__`today`}` },
                        { value: 'yesterday', label: `${__`yesterday`}` },
                        { value: 'lastSevenDays', label: `${__`last_7_days`}` },
                        { value: 'lastThirtyDays', label: `${__`last_30_days`}` },
                        { value: 'lastMonth', label: `${__`last_month`}` },
                        { value: 'lastYear', label: `${__`last_year`}` },
                    ]}
                    placeholder={__`select_a_period`}
                    onClear={() => setSelectedPeriod('')}
                    onChange={handlePeriodChange}
                    style={{ width: SM ? 150 : 'unset' }}
                    autoFocus
                    showSearch
                    allowClear
                />
                <DatePickerWrapper>
                    {
                        MD ? (
                            <DatePicker.RangePicker
                                defaultValue={[dayjs(startDate), dayjs(endDate)]}
                                value={[dayjs(startDate), dayjs(endDate)]}
                                style={{ width: '100%', height: 'fit-content' }}
                                onChange={value => {
                                    value?.[0] && onStartDateChange(dayjs(value[0]).toDate())
                                    value?.[1] && onEndDateChange(dayjs(value[1]).toDate())
                                }}
                                allowClear={false}
                                inputReadOnly
                            />
                        ) : (
                            <>
                                <DatePicker
                                    defaultValue={dayjs(startDate)}
                                    value={dayjs(startDate)}
                                    style={{ width: '100%', height: 'fit-content' }}
                                    onChange={value => onStartDateChange(dayjs(value).toDate())}
                                    allowClear={false}
                                    inputReadOnly
                                />
                                <DatePicker
                                    defaultValue={dayjs(endDate)}
                                    value={dayjs(endDate)}
                                    style={{ width: '100%', height: 'fit-content' }}
                                    onChange={value => onEndDateChange(dayjs(value).toDate())}
                                    allowClear={false}
                                    inputReadOnly
                                />
                            </>
                        )
                    }
                </DatePickerWrapper>
            </MenuWrapper>
        </MainWrapper>
    )
}
